import { useEffect, useState } from "react";
import { ExportOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Drawer, Empty, Skeleton, Space, Tabs } from "antd";
import { useNavigate } from "react-router-dom";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";

import { usePermission } from "~/hooks/usePermission";
import { useOptionalTraineeScope } from "~/modules/Trainee/TraineeProfile/scope";
import CustomerDetails from "~/modules/Products/ProductsCustomers/CustomerDetails";
import ProductClientDetails from "~/modules/Products/ProductClientDetails/ProductClientDetails";
import { supabase } from "~/store/initializeStore";

interface ClientPackageDrawerProps {
  /** Klientka, której pakiety pokazujemy — drawer sam dociąga listę, jeśli trzeba. */
  email: string | null;
  /** Pakiet otwierany na starcie; bez niego wchodzimy w pierwszy z listy. */
  productId?: string | null;
  onClose: () => void;
}

/**
 * Wspólny drawer dla dat dostępu na profilu i listy pakietów (TRE-1882).
 * Wcześniej oba miejsca nawigowały do `/products/:id/clients/:email`, które
 * siedzi za `products.manage ALL` — trener wpadał w cichy redirect na zakładkę
 * materiałów i wyglądało to jak wyrzucenie na stronę główną.
 *
 * Drawer osadza te same ekrany co trasy produktowe: szczegóły klientki w
 * pakiecie, a przy `products.manage` także widok bundla (customer). Resztę
 * przycinania rolą robią same ekrany.
 */
// Tyle wierszy brał `fetchProductClients` (listSize slice'a productClients).
const PACKAGES_LIMIT = 25;

// Drawer działa też poza profilem (windykacja), więc klucz idzie po e-mailu, a nie po karcie klientki.
const useClientPackages = (email: string | null) =>
  useQuery({
    queryKey: ["clientPackages", email],
    enabled: Boolean(email),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_client")
        .select("*")
        .order("createdAt", { ascending: false })
        .eq("email", email!)
        .range(0, PACKAGES_LIMIT - 1)
        .returns<ProductClient[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });

export const ClientPackageDrawer = ({ email, productId, onClose }: ClientPackageDrawerProps) => {
  const navigate = useNavigate();
  // Widok bundla to karta sprzedażowa klienta — aliasy, zamówienia, pakiety
  // ponad tę jedną klientkę. Trener zostaje przy szczegółach dostępu.
  const canManageProducts = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  const { data: clientPackages = [], isLoading } = useClientPackages(email);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const traineeScope = useOptionalTraineeScope();

  useEffect(() => {
    setSelectedProductId(null);
  }, [email, productId]);

  // Szczegóły pakietu (ProductClientDetails) siedzą na wspólnym slice `productClient` w Reduxie.
  // W karcie w tle muszą się odmontować, inaczej drawer otwarty w innej karcie nadpisze ten slice
  // i po powrocie pokazałyby pakiet innej klientki. Po powrocie montują się od nowa.
  if (!email || traineeScope?.isActive === false) {
    return null;
  }

  const selected =
    clientPackages.find((item) => item.productId === selectedProductId) ??
    clientPackages.find((item) => item.productId === productId) ??
    clientPackages[0] ??
    null;

  const renderBody = () => {
    if (!selected) {
      return isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych o pakiecie" />
      );
    }

    const accessDetails = (
      <ProductClientDetails embedded readonly={false} productId={selected.productId} clientEmail={selected.id} />
    );

    if (!canManageProducts) {
      return accessDetails;
    }

    return (
      <Tabs
        destroyInactiveTabPane
        items={[
          {
            key: "access",
            label: "Szczegóły dostępu",
            children: accessDetails,
          },
          {
            key: "customer",
            label: "Profil klienta",
            children: selected.customerId ? (
              <CustomerDetails embedded customerId={selected.customerId} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pakiet nie jest spięty z profilem klienta" />
            ),
          },
        ]}
      />
    );
  };

  // Trasa `/products/:id/clients/:email` siedzi za `products.manage ALL` —
  // trenerowi nie pokazujemy przejścia, bo wpadłby w cichy redirect (patrz
  // komentarz wyżej, TRE-1882).
  const standaloneUrl = canManageProducts && selected ? `/products/${selected.productId}/clients/${selected.id}` : null;

  return (
    <Drawer
      open
      width="min(1100px, 92vw)"
      onClose={onClose}
      destroyOnClose
      styles={{ body: { paddingTop: 8 } }}
      title={selected?.product?.name || selected?.accessPeriod?.productName || "Pakiet klientki"}
      extra={
        standaloneUrl ? (
          <Space size={0}>
            <Button
              type="link"
              onClick={() => {
                onClose();
                navigate(standaloneUrl);
              }}
            >
              Otwórz jako osobny ekran
            </Button>
            <Button
              style={{ marginLeft: -12 }}
              icon={<ExportOutlined />}
              title="Otwórz w nowym oknie"
              href={`${window.location.origin}${standaloneUrl}`}
              target="_blank"
              type="link"
            />
          </Space>
        ) : null
      }
    >
      {renderBody()}
    </Drawer>
  );
};
